import React from 'react';
import { ShieldCheck, Award, MapPin, CheckCircle2, Star, Users, Check } from 'lucide-react';

export const TrustProofBoard: React.FC = () => {
  const caseStudies = [
    {
      location: 'Umuahia, Abia State',
      client: 'Dr. Okoro Residence',
      system: '5kVA Hybrid Inverter + 10kWh LiFePO4',
      result: 'Zero generator usage for 14 months. Saved over ₦2.4M in petrol & diesel.',
      badge: 'Residential Premier',
    },
    {
      location: 'Lagos (Lekki Phase 1)',
      client: 'TechHub Co-working Space',
      system: '10kVA Three-Phase Deye + 20kWh Battery',
      result: 'Maintains 24/7 uninterrupted high-speed internet and AC workstations during grid outages.',
      badge: 'Commercial Hub',
    },
    {
      location: 'Abuja (Maitama)',
      client: 'Engr. & Mrs. Salami',
      system: '7.5kVA Executive Hybrid System',
      result: 'Seamless auto-switchover from PHCN to Solar & Battery with zero downtime.',
      badge: 'Executive Villa',
    },
    {
      location: 'Port Harcourt (GRA Phase 2)',
      client: 'Pharm. Aliyu Medical Store',
      system: '5kVA Reliable Solar Array',
      result: 'Uninterrupted cold-chain refrigeration for temperature-sensitive pharmaceuticals.',
      badge: 'Medical Retail',
    },
  ];

  const trustBadges = [
    {
      title: 'COREN & NSE Certified Engineers',
      desc: 'All installations supervised and certified by Council for Regulation of Engineering in Nigeria (COREN) professionals.',
      icon: <Award className="w-6 h-6 text-amber-400" />,
    },
    {
      title: '5-Year Lithium Warranty',
      desc: 'Grade-A LiFePO4 prismatic cells with a 6,000+ cycle lifespan and real-time smart BMS monitoring.',
      icon: <ShieldCheck className="w-6 h-6 text-emerald-400" />,
    },
    {
      title: '25-Year Panel Performance Guarantee',
      desc: 'Tier-1 monocrystalline half-cut solar panels engineered to withstand harsh harmattan dust and intense tropical heat.',
      icon: <Star className="w-6 h-6 text-cyan-400" />,
    },
    {
      title: 'Nationwide Deployment & Support',
      desc: 'Rapid installation teams across Lagos, Abuja, Port Harcourt, Umuahia, Ibadan, and Onitsha with 24/7 remote monitoring.',
      icon: <Users className="w-6 h-6 text-teal-400" />,
    },
  ];

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-10 shadow-2xl text-white space-y-12 my-12">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold uppercase tracking-wider">
          <ShieldCheck className="w-4 h-4" /> Anti-Scam Trust & Verified Proof
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
          Why Nigerians Trust Access Tech & Renewable Energy
        </h2>
        <p className="text-slate-400 text-sm">
          We combine tier-1 hardware engineering with strict COREN standards, transparent pricing, and 5-year warranty protection.
        </p>
      </div>

      {/* Trust Badges Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {trustBadges.map((badge, idx) => (
          <div key={idx} className="bg-slate-950 p-6 rounded-2xl border border-slate-800 space-y-3 hover:border-emerald-500/50 transition-all">
            <div className="w-12 h-12 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center">
              {badge.icon}
            </div>
            <h4 className="font-bold text-white text-base">{badge.title}</h4>
            <p className="text-xs text-slate-400 leading-relaxed">{badge.desc}</p>
          </div>
        ))}
      </div>

      {/* Customer Case Studies Section */}
      <div className="space-y-6 pt-6 border-t border-slate-800">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2">
              <MapPin className="w-6 h-6 text-emerald-400" />
              Verified Nigerian Case Studies & Installations
            </h3>
            <p className="text-xs sm:text-sm text-slate-400 mt-1">
              Real projects delivered across Umuahia, Lagos, Abuja, and Port Harcourt.
            </p>
          </div>
          <span className="text-xs px-3.5 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 font-bold">
            100% Success Rate
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {caseStudies.map((study, index) => (
            <div key={index} className="bg-slate-950 p-6 rounded-2xl border border-slate-800 flex flex-col justify-between space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between items-start">
                  <span className="text-xs font-bold text-emerald-400 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> {study.location}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-slate-900 text-slate-300 font-bold border border-slate-800">
                    {study.badge}
                  </span>
                </div>
                <h4 className="font-bold text-white text-base">{study.client}</h4>
                <div className="text-xs text-amber-400 font-semibold">{study.system}</div>
              </div>

              <div className="bg-slate-900 p-3.5 rounded-xl border border-slate-800 flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <p className="text-xs text-slate-300 leading-relaxed">{study.result}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
